import { Formik } from "formik";
import { useId } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@volca/ui/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@volca/ui/components/ui/input-group";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { currencySymbol, useReportCurrency } from "@/profitability/currency";
import { reportError } from "@/profitability/utils";
import { MapEditor } from "./map-editor";
import { submitSettingsForm } from "./submit-settings-form";
import type { TemplateSetting, TemplateSettingValue } from "./types";
import {
  effectiveValue,
  formatSetting,
  fromInput,
  fromMapRows,
  toInput,
  toMapRows,
} from "./values";

export const SettingField = ({
  setting,
  editable,
  onSave,
  onCancel,
}: {
  setting: TemplateSetting;
  editable: boolean;
  onSave: (value: TemplateSettingValue) => Promise<void>;
  onCancel: () => void;
}) => {
  const id = useId();
  const currency = useReportCurrency();
  const initialValue = effectiveValue(setting);
  const initialValues = {
    text: setting.type === "map" ? "" : toInput(setting.type, initialValue),
    rows: toMapRows(setting.value_type ?? "number", initialValue),
    useDefault: setting.value === null,
  };
  const parse = (values: typeof initialValues) =>
    values.useDefault
      ? { ok: true as const, value: null }
      : setting.type === "map"
        ? fromMapRows(setting.value_type ?? "number", values.rows)
        : fromInput(setting.type, values.text);
  if (!editable)
    return <Text>{formatSetting(setting, initialValue, currency)}</Text>;
  return (
    <Formik
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validate={(values) => {
        const parsed = parse(values);
        return parsed.ok ? {} : { text: parsed.error };
      }}
      onSubmit={async (values, helpers) => {
        const parsed = parse(values);
        if (!parsed.ok) return;
        helpers.setStatus(undefined);
        try {
          await onSave(parsed.value);
          onCancel();
        } catch (error) {
          helpers.setStatus(
            `Could not save. ${reportError(error)} Your changes are still here. Try again.`,
          );
        }
      }}
    >
      {({
        values,
        errors,
        status,
        isSubmitting,
        submitForm,
        setFieldValue,
        setValues,
        setFieldError,
      }) => {
        const error = errors.text;
        const mapResult =
          setting.type === "map" && error
            ? fromMapRows(setting.value_type ?? "number", values.rows)
            : undefined;
        const describedBy = `${id}-description${error ? ` ${id}-error` : ""}`;
        return (
          <form onSubmit={(event) => submitSettingsForm(event, submitForm)}>
            <Stack gap={5}>
              <Field
                data-invalid={(setting.type !== "map" && !!error) || undefined}
              >
                <FieldLabel htmlFor={setting.type === "map" ? undefined : id}>
                  {setting.label}
                </FieldLabel>
                {setting.type === "map" ? (
                  <MapEditor
                    setting={setting}
                    rows={values.rows}
                    disabled={isSubmitting}
                    invalid={mapResult && !mapResult.ok ? mapResult : undefined}
                    describedBy={`${id}-description`}
                    errorId={error ? `${id}-error` : undefined}
                    onChange={(rows) => {
                      void setFieldValue("rows", rows);
                      void setFieldValue("useDefault", false);
                      setFieldError("text", undefined);
                    }}
                  />
                ) : (
                  <InputGroup>
                    {setting.type === "money" && (
                      <InputGroupAddon>
                        <InputGroupText>
                          {currencySymbol(currency)}
                        </InputGroupText>
                      </InputGroupAddon>
                    )}
                    <InputGroupInput
                      id={id}
                      aria-invalid={!!error || undefined}
                      aria-describedby={describedBy}
                      type={setting.type === "date" ? "date" : "text"}
                      inputMode={
                        setting.type === "text" || setting.type === "date"
                          ? undefined
                          : "decimal"
                      }
                      value={values.text}
                      disabled={isSubmitting}
                      onChange={(event) => {
                        void setFieldValue("text", event.target.value);
                        void setFieldValue("useDefault", false);
                        setFieldError("text", undefined);
                      }}
                    />
                    {setting.type === "percent" && (
                      <InputGroupAddon align="inline-end">
                        <InputGroupText>%</InputGroupText>
                      </InputGroupAddon>
                    )}
                  </InputGroup>
                )}
                {error && <FieldError id={`${id}-error`}>{error}</FieldError>}
                <FieldDescription id={`${id}-description`}>
                  {setting.description}
                </FieldDescription>
              </Field>
              <Stack gap={2}>
                {setting.type !== "map" && (
                  <Text variant="caption" tone="muted">
                    Default: {formatSetting(setting, setting.default, currency)}
                    {values.useDefault ? " (in use)" : ""}
                  </Text>
                )}
                {!values.useDefault && (
                  <Stack direction="row">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={isSubmitting}
                      onClick={() => {
                        void setValues({
                          text:
                            setting.type === "map"
                              ? ""
                              : toInput(setting.type, setting.default),
                          rows: toMapRows(
                            setting.value_type ?? "number",
                            setting.default,
                          ),
                          useDefault: true,
                        });
                        setFieldError("text", undefined);
                      }}
                    >
                      Reset to default
                    </Button>
                  </Stack>
                )}
              </Stack>
              {typeof status === "string" && (
                <Text role="alert" tone="destructive">
                  {status}
                </Text>
              )}
              <Stack direction="row" gap={2} justify="end">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={onCancel}
                >
                  Cancel
                </Button>
                <Button type="submit" loading={isSubmitting}>
                  Save
                </Button>
              </Stack>
            </Stack>
          </form>
        );
      }}
    </Formik>
  );
};
